import { isBefore, startOfDay } from 'date-fns';
import React, { useMemo } from 'react';
import { ScheduleDateField } from 'shared/components/ui/schedule-date-field';
import { RRule } from 'shared/lib/rrule';
import { DateFormatOptions } from 'shared/types/date-format-options';
import { HabitSchedule } from 'shared/types/habit-schedule';
import { Timestamp } from 'shared/types/timestamp';
import { WeekDays } from 'shared/types/week-days';
import { getActiveSchedule } from 'shared/utils/get-active-schedule';

export type ScheduleStartDateInputProps = {
  schedules: HabitSchedule[];
  onChange: (schedules: HabitSchedule[] | null) => void;
  weekStartsOn: WeekDays;
  dateFormat: DateFormatOptions;
  today: Timestamp;
};

export const ScheduleStartDateInput: React.FC<ScheduleStartDateInputProps> = ({
  schedules,
  weekStartsOn,
  dateFormat,
  today,
  onChange,
}) => {
  const activeSchedule = useMemo(
    () => getActiveSchedule(schedules, today),
    [schedules, today],
  );

  const occurrences = useMemo(() => {
    if (!activeSchedule?.rrule) {
      return [];
    }

    const rrule = new RRule({
      ...RRule.fromString(activeSchedule.rrule.format).origOptions,
      dtstart: startOfDay(activeSchedule.startDate),
      count: 20,
    });

    return rrule.all();
  }, [activeSchedule?.rrule, activeSchedule?.startDate]);

  const onChangeDate = (startDate: Date | null) => {
    if (!startDate || isBefore(startDate, today)) {
      return;
    }

    const newSchedules = structuredClone(schedules);
    const scheduleToUpdate = getActiveSchedule(newSchedules, today);

    if (!scheduleToUpdate) {
      return;
    }

    scheduleToUpdate.startDate = startDate;

    onChange(newSchedules);
  };

  return (
    <ScheduleDateField
      value={activeSchedule?.startDate}
      onChange={onChangeDate}
      today={today}
      weekStartsOn={weekStartsOn}
      dateFormat={dateFormat}
      minDate={today}
      highlightDates={occurrences}
    />
  );
};
